import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, NeuralChatApiError } from "../api/client";
import { CurrentMember } from "../types";

export type AuthStatus = "initializing" | "authenticated" | "unauthenticated";

type AuthContextValue = {
  status: AuthStatus;
  member: CurrentMember | null;
  error: string;
  login: (identifier: string, password: string) => Promise<CurrentMember>;
  logout: () => Promise<void>;
  refreshMe: (signal?: AbortSignal) => Promise<CurrentMember | null>;
  markUnauthenticated: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const NeuralChatAuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [status, setStatus] = useState<AuthStatus>("initializing");
  const [member, setMember] = useState<CurrentMember | null>(null);
  const [error, setError] = useState("");

  const refreshMe = useCallback(async (signal?: AbortSignal) => {
    try {
      const current = await api.me(signal);
      setMember(current);
      setStatus("authenticated");
      setError("");
      return current;
    } catch (reason) {
      if (reason instanceof DOMException && reason.name === "AbortError") throw reason;
      if (reason instanceof NeuralChatApiError && reason.status === 401) {
        setMember(null);
        setStatus("unauthenticated");
        return null;
      }
      setStatus("unauthenticated");
      setError("NeuralChat 인증 상태를 확인하지 못했습니다.");
      return null;
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refreshMe(controller.signal);
    return () => controller.abort();
  }, [refreshMe]);

  const login = useCallback(async (identifier: string, password: string) => {
    await api.login(identifier, password);
    const current = await api.me();
    setMember(current);
    setStatus("authenticated");
    setError("");
    return current;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // The backend logout is session-flushing; local state is cleared regardless.
    } finally {
      setMember(null);
      setStatus("unauthenticated");
    }
  }, []);

  const markUnauthenticated = useCallback(() => {
    setMember(null);
    setStatus("unauthenticated");
  }, []);

  const value = useMemo(
    () => ({ status, member, error, login, logout, refreshMe, markUnauthenticated }),
    [status, member, error, login, logout, refreshMe, markUnauthenticated],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useNeuralChatAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useNeuralChatAuth must be used inside NeuralChatAuthProvider");
  return value;
};
